import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { FlowService } from '../services/beneficiaries-flow.service';
import { StorageService } from '../services/storage.service';
import { BeneficiariosService } from '../services/beneficiarios.service';
import { StepAccessConstants } from '../constants/step-access.constants';
import { Access } from '../types/access.type';

export const stepAccessGuard: CanActivateFn = (route) => {
  const beneficiariesService = inject(BeneficiariosService);
  const flowService = inject(FlowService);
  const router = inject(Router);
  const storageService = inject(StorageService);

  const access = (route.data?.[StepAccessConstants.ACCESS_KEY] ?? StepAccessConstants.PUBLIC_KEY) as Access;
  const goTo = (segments: any[]) => router.createUrlTree(segments);

  if (access === StepAccessConstants.PUBLIC_KEY) return true;

  if (access === StepAccessConstants.REQUIRES_IDENTIFIED_KEY) {
    return flowService.canEnterVerify() ? true : goTo(['/beneficiarios/registro', flowService.lastAllowedChildPath()]);
  }

  const ensureServerBeneficiaries$ = () => {
    const identity = storageService.getIdentityInformation();
    const certificateNumber = identity?.certificateNumber;

    return beneficiariesService.getClientBeneficiaries(certificateNumber).pipe(
      map((res: any) => {
        const dbBeneficiaries = res ?? [];

        if (dbBeneficiaries.length > 0) storageService.setBeneficiariesList(dbBeneficiaries);

        return { hasServer: dbBeneficiaries.length > 0, list: dbBeneficiaries };
      })
    );
  };

  if (access === StepAccessConstants.REQUIRES_OTP_KEY || access === StepAccessConstants.REQUIRES_ADDED_KEY) {
    if (!flowService.canEnterNuevoBeneficiarioBase()) {
      return goTo(['/beneficiarios/registro', flowService.lastAllowedChildPath()]);
    }

    return ensureServerBeneficiaries$().pipe(
      map(({ hasServer }) => {
        if (hasServer) {
          return goTo(['/beneficiarios/resumen']);
        }

        if (access === StepAccessConstants.REQUIRES_OTP_KEY) {
          if (flowService.getPostOtpStep() === StepAccessConstants.SUMMARY_KEY) {
            return flowService.consumeFormNonce() ? true : goTo(['/beneficiarios/registro', 'revision']);
          }

          return true;
        }

        if (!flowService.canEnterRevisionBase()) {
          return goTo(['/beneficiarios/registro', flowService.lastAllowedChildPath()]);
        }

        return flowService.consumeSummaryNonce() ? true : goTo(['/beneficiarios/registro', 'nuevo-beneficiario']);
      })
    );
  }

  if ((access as any) === 'requiresCompleted') {
    const s = flowService.current();
    return (s === 'otpVerified' || s === 'completed')
      ? true
      : goTo(['/beneficiarios/registro', flowService.lastAllowedChildPath()]);
  }

  return goTo(['/beneficiarios/registro', flowService.lastAllowedChildPath()]);
};
